import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { BurgerMenu } from './BurgerMenu';
import { TopBar } from './TopBar';
import { isAuthenticated, logout } from '../authUtils';
import { WorkflowBarProvider } from '../contexts/WorkflowBarContext';
import { CarrierEngagementFilterProvider } from '../contexts/CarrierEngagementFilterContext';
import { useIdleTimeout } from '../hooks/useIdleTimeout';

/**
 * AuthenticatedShell - Inner layout rendered only when the user is authenticated.
 *
 * The idle-timeout hook lives here (not in AppShell) so it only mounts for real
 * authenticated sessions. Mounting it above the auth check would cause the hook
 * to fire logout() for unauthenticated renders.
 */
function AuthenticatedShell() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setSearchText('');
    setSearchDraft('');
    setIsSearchOpen(false);
  }, [location.pathname]);

  useIdleTimeout(logout);

  const handleSearchSubmit = () => {
    setSearchText(searchDraft);
  };

  const handleSearchClear = () => {
    setSearchText('');
    setSearchDraft('');
    setIsSearchOpen(false);
  };

  return (
    <WorkflowBarProvider>
      <CarrierEngagementFilterProvider>
        <div className="sla-app-theme flex h-screen flex-col overflow-hidden bg-background">
          {/* Top Bar */}
          <TopBar
            onMenuOpen={() => setMenuOpen(true)}
            isSearchOpen={isSearchOpen}
            setIsSearchOpen={setIsSearchOpen}
            searchDraft={searchDraft}
            setSearchDraft={setSearchDraft}
            searchText={searchText}
            onSearchSubmit={handleSearchSubmit}
            onSearchClear={handleSearchClear}
          />

          {/* Hamburger Menu Drawer */}
          <BurgerMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

          {/* Page Content */}
          <main className="flex-1 overflow-y-auto bg-muted/10 p-6">
            <Outlet context={{ searchText }} />
          </main>
        </div>
      </CarrierEngagementFilterProvider>
    </WorkflowBarProvider>
  );
}

/**
 * AppShell - Main layout component with top bar and hamburger menu.
 * Uses Outlet from react-router-dom to render child routes.
 *
 * Acts as an authentication guard - redirects to login if not authenticated.
 * Defers all session-aware behavior (idle timeout, state) to AuthenticatedShell
 * so those hooks only mount for authenticated renders.
 */
function AppShell() {
  const location = useLocation();

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <AuthenticatedShell />;
}

export { AppShell };
